import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { get } from 'lodash';

import {
  AssetVisitDirection,
  AssetVisitEventType,
  AssetVisitType,
  formatDateTimeWithMilitarySupport,
  getDirectionForVisitType,
  LuxonDateTimeFormats
} from '@nova/core';
import { nestedCompanyPresenterDataProps } from '@satellite/composables/presenterV2NestedCompanyProps';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { reactiveOption } from '@satellite/composables/presenterV2ReactiveOption';
import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';

import type { ComputedRef, Ref } from 'vue';
import type { ComposerTranslation } from 'vue-i18n';
import type { Maybe } from '@nova/core';
import type {
  PresenterV2DisplayConfigOptions,
  PresenterV2DisplayOverrideProps,
  PresenterV2DisplaySize
} from '@satellite/composables/presenterV2DisplayOptions';
import type { YardAssetVisitDetails } from './yardAssetVisitPresenterV2Shared';

export type YardAssetVisitDisplayField =
  | 'arrivedTime'
  | 'companyName'
  | 'companyScac'
  | 'dockedAt'
  | 'dotNumber'
  | 'driverAppointmentIdentifier'
  | 'driverNotes'
  | 'licensePlate'
  | 'phone'
  | 'stateIcon'
  | 'truckIcon'
  | 'visitType'
  | 'visitTypeChip'
  | 'visitTypeIcon';

export interface UseYardAssetVisitDisplayV2Options extends PresenterV2DisplayConfigOptions {
  arrivedTimeProps?: PresenterV2DisplayOverrideProps;
  companyNameProps?: PresenterV2DisplayOverrideProps;
  companyScacProps?: PresenterV2DisplayOverrideProps;
  dockedAtProps?: PresenterV2DisplayOverrideProps;
  dotNumberProps?: PresenterV2DisplayOverrideProps;
  driverAppointmentIdentifierProps?: PresenterV2DisplayOverrideProps;
  driverNotesProps?: PresenterV2DisplayOverrideProps;
  licensePlateProps?: PresenterV2DisplayOverrideProps;
  phoneProps?: PresenterV2DisplayOverrideProps;
  stateIconProps?: PresenterV2DisplayOverrideProps;
  truckIconProps?: PresenterV2DisplayOverrideProps;
  visitTypeIconProps?: PresenterV2DisplayOverrideProps;
  visitTypeProps?: PresenterV2DisplayOverrideProps;
}

type DisplaySize = PresenterV2DisplaySize;

const VISIT_TYPE_TO_I18N_KEY = {
  [AssetVisitType.Live]: 'live',
  [AssetVisitType.LiveUnload]: 'live',
  [AssetVisitType.LiveLoad]: 'live',
  [AssetVisitType.Drop]: 'drop',
  [AssetVisitType.PickUp]: 'pickup',
  [AssetVisitType.DropHook]: 'dropHook'
};

const DIRECTION_TO_I18N_KEY = {
  [AssetVisitDirection.Inbound]: 'inbound',
  [AssetVisitDirection.Outbound]: 'outbound',
  [AssetVisitDirection.InboundAndOutbound]: 'inboundAndOutbound'
};

function chipSizeForDisplay(size: DisplaySize) {
  return size === 'small' ? 'x-small' : 'small';
}

function chipTextTypographyClass(size: DisplaySize) {
  return {
    'typography-chip-sm': size === 'small',
    'typography-caption-bold': ['regular', 'large'].includes(size),
    'text--color-text-neutral-90': true
  };
}

function translateVisitTypeDisplayName(t: ComposerTranslation, type: Maybe<string>) {
  const typeKey = type ? VISIT_TYPE_TO_I18N_KEY[type as AssetVisitType] : undefined;
  if (!typeKey) {
    return { type, direction: null };
  }

  const typeName = t(`core.yard.assetVisitTypes.${typeKey}`);
  const direction = getDirectionForVisitType(type as AssetVisitType);
  if (!direction) {
    return { type: typeName, direction: null };
  }

  const dirKey = DIRECTION_TO_I18N_KEY[direction];
  const directionName = t(`core.yard.assetVisitDirections.${dirKey}`);
  return { type: typeName, direction: directionName };
}

function resolveVisitTypeIcon(type: Maybe<string>) {
  if (type === AssetVisitType.PickUp) {
    return 'mdi-truck-flatbed';
  }

  if ([AssetVisitType.Drop, AssetVisitType.DropHook].includes(type as AssetVisitType)) {
    return 'mdi-truck-cargo-container';
  }

  return 'mdi-truck-outline';
}

function formatVisitDateTime(dateTime: string, warehouseTimezone: Maybe<string>) {
  return formatDateTimeWithMilitarySupport(
    dateTime,
    warehouseTimezone,
    LuxonDateTimeFormats.ShortMonthYearDayTimeAMPM,
    false,
    LuxonDateTimeFormats.ShortMonthYearDayTime24
  );
}

function getDockedAtFromVisit(visit: YardAssetVisitDetails | null): string | null {
  if (!visit) {
    return null;
  }

  const latestDockedEvent = (visit.assetVisitEvents ?? [])
    .filter(event => event?.eventType === AssetVisitEventType.Docked)
    .sort((left, right) => {
      const leftTs = new Date(get(left, 'createDateTime') ?? 0).getTime();
      const rightTs = new Date(get(right, 'createDateTime') ?? 0).getTime();

      return rightTs - leftTs;
    })[0];

  const createDateTime = (latestDockedEvent as { createDateTime?: string | Date } | undefined)
    ?.createDateTime;
  return createDateTime ? String(createDateTime) : null;
}

export function useYardAssetVisitDisplayV2(
  visit: Ref<YardAssetVisitDetails | null> | ComputedRef<YardAssetVisitDetails | null>,
  options: UseYardAssetVisitDisplayV2Options = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterV2Mode.Live);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? null);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const visitTypeRaw = computed(() => visit.value?.visitType ?? null);
  const visitTypeDisplay = computed(() => translateVisitTypeDisplayName(t, visitTypeRaw.value));
  const visitTypeLabel = computed(() => visitTypeDisplay.value.type);
  const visitDirection = computed(() => visitTypeDisplay.value.direction);
  const visitTypeIcon = computed(() => resolveVisitTypeIcon(visitTypeRaw.value));
  const phone = computed(() => visit.value?.phone ?? null);
  const company = computed(() => visit.value?.company ?? null);
  const companyHint = computed(() => visit.value?.companyHint ?? null);
  const licensePlate = computed(() => visit.value?.licensePlate ?? null);
  const dotNumber = computed(() => visit.value?.dotNumber ?? null);
  const driverNotes = computed(() => visit.value?.driverNotes ?? null);
  const driverAppointmentIdentifier = computed(
    () => visit.value?.driverAppointmentIdentifier ?? null
  );
  const arrivedTime = computed(() => visit.value?.createDateTime ?? null);
  const dockedAt = computed(() => getDockedAtFromVisit(visit.value));
  const companyName = computed(() => company.value?.name ?? companyHint.value ?? null);
  const companyScac = computed(() => company.value?.scac ?? null);
  const visitTypeChipText = computed(() => {
    const { type, direction } = visitTypeDisplay.value;
    return direction ? `${type} \u2022 ${direction}` : type;
  });

  const arrivedTimeFormatted = computed(() =>
    arrivedTime.value
      ? (formatVisitDateTime(arrivedTime.value, effectiveTimezone.value) ??
        PRESENTER_VALUE_FALLBACK)
      : null
  );
  const dockedAtFormatted = computed(() =>
    dockedAt.value
      ? (formatVisitDateTime(dockedAt.value, effectiveTimezone.value) ?? PRESENTER_VALUE_FALLBACK)
      : null
  );

  const companyPresenterProps = computed(() =>
    nestedCompanyPresenterDataProps(displayMode.value, {
      company: company.value
    })
  );

  const companyNameAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-company-name' },
      toValue(options.companyNameProps) ?? {}
    )
  );
  const companyScacAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-company-scac' },
      toValue(options.companyScacProps) ?? {}
    )
  );
  const visitTypeAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-visit-type' },
      toValue(options.visitTypeProps) ?? {}
    )
  );
  const visitTypeChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    variant: 'tonal',
    'data-testid': 'yard-asset-visit-presenter-visit-type-chip-shell'
  }));
  const visitTypeChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-visit-presenter-visit-type-chip'
  }));
  const phoneAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-phone' },
      toValue(options.phoneProps) ?? {}
    )
  );
  const driverNotesAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-driver-notes' },
      toValue(options.driverNotesProps) ?? {}
    )
  );
  const arrivedTimeAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-arrived-time' },
      toValue(options.arrivedTimeProps) ?? {}
    )
  );
  const dockedAtAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-docked-at' },
      toValue(options.dockedAtProps) ?? {}
    )
  );
  const driverAppointmentIdentifierAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-driver-appointment-identifier' },
      toValue(options.driverAppointmentIdentifierProps) ?? {}
    )
  );
  const licensePlateAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-license-plate' },
      toValue(options.licensePlateProps) ?? {}
    )
  );
  const dotNumberAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-dot-number' },
      toValue(options.dotNumberProps) ?? {}
    )
  );
  const visitTypeIconAttrs = computed(() =>
    mergeProps(
      {
        size: 20,
        icon: visitTypeIcon.value,
        'data-testid': 'yard-asset-visit-presenter-visit-type-icon'
      },
      toValue(options.visitTypeIconProps) ?? {}
    )
  );
  const truckIconAttrs = computed(() =>
    mergeProps(
      {
        size: 20,
        icon: visitTypeIcon.value,
        'data-testid': 'yard-asset-visit-presenter-truck-icon'
      },
      toValue(options.truckIconProps) ?? {}
    )
  );
  const stateIconAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-visit-presenter-state-icon' },
      toValue(options.stateIconProps) ?? {}
    )
  );

  return reactive({
    visit,
    mode: displayMode,
    size: displaySize,
    warehouseTimezone: effectiveTimezone,
    isFetching: isFetchingValue,
    visitTypeRaw,
    visitTypeLabel,
    visitDirection,
    visitTypeIcon,
    phone,
    company,
    companyHint,
    licensePlate,
    dotNumber,
    driverNotes,
    driverAppointmentIdentifier,
    arrivedTime,
    dockedAt,
    companyName,
    companyScac,
    visitTypeChipText,
    arrivedTimeFormatted,
    dockedAtFormatted,
    companyNameAttrs,
    companyScacAttrs,
    visitTypeAttrs,
    visitTypeChipAttrs,
    visitTypeChipTextAttrs,
    phoneAttrs,
    driverNotesAttrs,
    arrivedTimeAttrs,
    dockedAtAttrs,
    driverAppointmentIdentifierAttrs,
    licensePlateAttrs,
    dotNumberAttrs,
    visitTypeIconAttrs,
    truckIconAttrs,
    stateIconAttrs,
    companyPresenterProps
  });
}

export type YardAssetVisitDisplayV2Data = ReturnType<typeof useYardAssetVisitDisplayV2>;
